/** Shift type by time of day for monthly view pill styling. */
export type ShiftType = 'morning' | 'evening' | 'night'

/** Tailwind-compatible border + bg classes for monthly shift pills (left border + light bg). */
export const SHIFT_TYPE_PILL_CLASSES: Record<
  ShiftType,
  { border: string; bg: string }
> = {
  morning: { border: 'border-l-blue-500', bg: 'bg-blue-50 dark:bg-blue-950/30' },
  evening: { border: 'border-l-orange-500', bg: 'bg-orange-50 dark:bg-orange-950/30' },
  night: { border: 'border-l-purple-500', bg: 'bg-purple-50 dark:bg-purple-950/30' },
} as const

/**
 * Derives shift type from startTime (HH:mm or HH:mm:ss). Morning < 12:00, evening 12:00–17:00, night >= 17:00.
 */
export function getShiftTypeFromStartTime(startTime: string): ShiftType {
  const match = startTime.trim().match(/^(\d{1,2}):(\d{2})/)
  if (!match) return 'morning'
  const hours = parseInt(match[1], 10)
  if (hours < 12) return 'morning'
  if (hours < 17) return 'evening'
  return 'night'
}

/** Assignment status for shift assignments; aligns with ShiftAssignment.status. */
export type AssignmentStatus = 'scheduled' | 'confirmed' | 'cancelled'

/** Background colors for shift assignment blocks by status. */
export const ASSIGNMENT_STATUS_COLORS: Record<AssignmentStatus, string> = {
  confirmed: '#15803d',
  scheduled: '#ca8a04',
  cancelled: '#6b7280',
} as const

/**
 * Returns the background color for a shift assignment block by status.
 * Defaults to scheduled if status is unknown.
 */
export function getAssignmentStatusColor(status: AssignmentStatus | string): string {
  if (status === 'confirmed' || status === 'scheduled' || status === 'cancelled') {
    return ASSIGNMENT_STATUS_COLORS[status]
  }
  return ASSIGNMENT_STATUS_COLORS.scheduled
}

/**
 * Returns 'light' (white text) or 'dark' (dark text) for the given background color.
 * Handles hex (3/6/8) and rgb/rgba(). On parse failure returns 'dark' so text stays readable.
 */
export function getContrastTextVariant(cssColor: string): 'light' | 'dark' {
  const rgb = parseColor(cssColor)
  if (!rgb) return 'dark'
  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b)
  return luminance > 0.35 ? 'dark' : 'light'
}

function parseColor(value: string): { r: number; g: number; b: number } | null {
  const trimmed = value.trim()
  const hex = hexToRgb(trimmed)
  if (hex) return hex
  return parseRgbRgba(trimmed)
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.startsWith('#') ? hex.slice(1).trim() : hex.trim()
  const len = normalized.length
  if (len === 3) {
    const r = parseInt(normalized[0] + normalized[0], 16)
    const g = parseInt(normalized[1] + normalized[1], 16)
    const b = parseInt(normalized[2] + normalized[2], 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return { r, g, b }
  }
  if (len === 6 || len === 8) {
    const r = parseInt(normalized.slice(0, 2), 16)
    const g = parseInt(normalized.slice(2, 4), 16)
    const b = parseInt(normalized.slice(4, 6), 16)
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null
    return { r, g, b }
  }
  return null
}

function parseRgbRgba(value: string): { r: number; g: number; b: number } | null {
  const match = value.match(
    /^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)$/
  )
  if (!match) return null
  const r = Math.min(255, Math.max(0, parseInt(match[1], 10)))
  const g = Math.min(255, Math.max(0, parseInt(match[2], 10)))
  const b = Math.min(255, Math.max(0, parseInt(match[3], 10)))
  return { r, g, b }
}

function getRelativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}
